import type { 
  ArchetypeAnalysis, 
  RecipientAnalysis, 
  FinalRecommendations,
  OpenAIArchetypeRequest, 
  OpenAIRecipientRequest,
  OpenAIRecommendationRequest,
  GiverArchetypeKey, 
  RecipientArchetypeKey,
  Answers
} from '../types';

export async function analyzeGiftGivingArchetype(request: OpenAIArchetypeRequest): Promise<ArchetypeAnalysis> {
  try {
    const response = await fetch('/api/analyze-archetype', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const analysis: ArchetypeAnalysis = await response.json();
    return analysis;

  } catch (error) {
    console.error('Error analyzing archetype with backend API:', error);
    
    // Fallback to rule-based system if backend fails
    return getFallbackGiverArchetype(request.answers);
  }
}

export async function analyzeRecipientArchetype(request: OpenAIRecipientRequest): Promise<RecipientAnalysis> {
  try {
    // Check if we're in development mode and the API might not be available
    const isDevelopment = import.meta.env.DEV;
    
    const response = await fetch('/api/analyze-recipient', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      if (isDevelopment && response.status === 404) {
        console.warn('API endpoint not available in development mode. Using fallback analysis. Run "npm run dev:api" to use full API functionality.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const analysis: RecipientAnalysis = await response.json();
    return analysis;

  } catch (error) {
    const isDevelopment = import.meta.env.DEV;
    if (isDevelopment) {
      console.warn('🚧 Development Mode: Using fallback recipient analysis. For full AI analysis, run "npm run dev:api" instead of "npm run dev"');
    } else {
      console.error('Error analyzing recipient archetype with backend API:', error);
    }
    
    // Fallback to simple analysis if backend fails
    return getFallbackRecipientArchetype(request.answers);
  }
}

export async function generateFinalRecommendations(request: OpenAIRecommendationRequest): Promise<FinalRecommendations> {
  try {
    const response = await fetch('/api/generate-recommendations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const recommendations: FinalRecommendations = await response.json();
    return recommendations;

  } catch (error) {
    console.error('Error generating final recommendations with backend API:', error);
    
    // Fallback to simple recommendations if backend fails
    return getFallbackRecommendations(request.giverProfile, request.recipientProfile, request.occasionContext);
  }
}

// Fallback functions for when the backend API is unavailable
function getFallbackGiverArchetype(answers: Answers): ArchetypeAnalysis {
  // Simple rule-based fallback analysis based on key patterns
  const hasHighAnxiety = answers['2'] === 'stressed';
  const isPractical = answers['5'] && Array.isArray(answers['5']) && answers['5'].some(v => 
    typeof v === 'object' && v !== null && 'text' in v && 
    (v.text as string).toLowerCase().includes('practical')
  );
  const isCreative = answers['3'] === 'unique';
  
  let primaryArchetype: GiverArchetypeKey = "Strategic Minimalist";
  const confidence = 65;
  
  if (hasHighAnxiety) {
    primaryArchetype = "Anxious Perfectionist";
  } else if (isPractical) {
    primaryArchetype = "Fixer/Provider";
  } else if (isCreative) {
    primaryArchetype = "Creative Crafter";
  }

  return {
    primaryArchetype,
    secondaryArchetype: undefined,
    confidence,
    reasoning: "Analysis based on fallback system using basic pattern recognition from key responses.",
    personalizedInsights: [
      "Your gift-giving approach shows thoughtfulness and care",
      "You tend to consider the recipient's needs and preferences",
      "You value meaningful connections through thoughtful gestures"
    ],
    giftSuggestionThemes: [
      "Practical items that solve problems or improve daily life",
      "Experiences that create lasting memories",
      "Personalized items that show attention to detail",
      "Quality items that demonstrate care and consideration"
    ]
  };
}

function getFallbackRecipientArchetype(answers: Answers): RecipientAnalysis {
  // Simple rule-based fallback for recipient analysis
  let primaryArchetype: RecipientArchetypeKey = "Useful Pragmatist";
  let confidence = 60;
  
  // Basic pattern detection from common question types
  if (answers['105'] === 'Experiences' || answers['211'] && String(answers['211']).toLowerCase().includes('experience')) {
    primaryArchetype = "Experiential Creative";
    confidence = 65;
  }

  return {
    primaryArchetype,
    secondaryArchetype: undefined,
    confidence,
    reasoning: "Analysis based on fallback system due to limited data processing capabilities.",
    personalizedInsights: [
      "They likely appreciate practical and functional gifts",
      "Quality and utility are probably more important than novelty",
      "They may prefer items that solve problems or improve daily life"
    ],
    giftPreferences: [
      "Practical tools and gadgets",
      "Quality everyday items", 
      "Useful services or subscriptions",
      "Problem-solving products"
    ],
    restrictionsAndPreferences: {
      selectedRestrictions: [],
      customRestrictions: null,
      engagementLevel: "unknown due to fallback mode",
      mustAvoid: [],
      preferredFormats: ["unknown due to fallback mode"]
    }
  };
}

function getFallbackRecommendations(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _giverProfile: { name: string; archetype: GiverArchetypeKey; traits: string[] }, 
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _recipientProfile: { name: string; archetype: RecipientArchetypeKey; traits: string[] }, 
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _occasionContext: { type: string; budget: string; emotionGoals: string[] }
): FinalRecommendations {
  return {
    recommendations: [
      {
        title: "Thoughtful Experience",
        description: "A curated experience that matches their interests and creates lasting memories.",
        reasoning: "Based on the psychological profiles, an experience-based gift would resonate with both the giver's desire to create meaningful connections and the recipient's preferences.",
        psychologicalFit: "This recommendation bridges the gap between the giver's approach and the recipient's preferences based on their archetypes.",
        category: "Experience",
        tags: ["memorable", "personal", "meaningful"]
      },
      {
        title: "Practical Luxury",
        description: "A high-quality item that serves a practical purpose while feeling special and indulgent.",
        reasoning: "Combines functionality with emotional significance, addressing both practical needs and the desire to feel valued.",
        psychologicalFit: "Balances the practical aspects appreciated by many recipients with the thoughtfulness desired by most givers.",
        category: "Physical", 
        tags: ["practical", "quality", "thoughtful"]
      },
      {
        title: "Personal Connection",
        description: "Something that reflects shared memories, inside jokes, or personal significance to your relationship.",
        reasoning: "Strengthens the emotional bond and shows deep understanding of the relationship dynamics.",
        psychologicalFit: "Addresses the psychological need for connection and understanding that underlies most meaningful gift exchanges.",
        category: "Personal",
        tags: ["sentimental", "personal", "relationship"]
      }
    ],
    psychologicalSummary: "The giver-recipient dynamic suggests a relationship built on mutual understanding and care. The recommendations balance emotional significance with practical value.",
    giverRecipientDynamic: "This pairing shows compatibility in values and approach to relationships, making gift-giving a natural expression of care and connection.",
    occasionFit: "The occasion calls for something that honors the relationship while creating positive memories and reinforcing the emotional bond."
  };
}

 